import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { NEW_UI_FALLBACK_ROUTE, NEW_UI_PUBLIC_LANDING } from './access';
import { LandingPage } from './landing/LandingPage';
import { NewUiApp } from './NewUiApp';
import { V2_LANDING } from './nav';
import { useNewUiAccess } from './useNewUiAccess';
import './newui.css';

/**
 * The one entry point registered in App.tsx (`<Route path="/v2/*">`).
 *
 * Composition, per DESIGN_PLAN §2.1:
 *   ProtectedRoute → useNewUiAccess() → Navigate(NEW_UI_FALLBACK_ROUTE) when
 *   not allowlisted → otherwise the landing page or the app shell.
 *
 * With `NEW_UI_PUBLIC_LANDING = true` the bare `/v2` landing route is hoisted
 * outside `ProtectedRoute`; everything under `/v2/app` stays gated regardless.
 *
 * Route paths below are relative — this component is mounted at `/v2/*`.
 */

/** Signed in AND allowlisted, or you go back to v1. */
const RequireV2Access: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute>
    <AllowlistGate>{children}</AllowlistGate>
  </ProtectedRoute>
);

const AllowlistGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { allowed } = useNewUiAccess();
  if (!allowed) return <Navigate to={NEW_UI_FALLBACK_ROUTE} replace />;
  return <>{children}</>;
};

export const NewUiEntry: React.FC = () => (
  <Routes>
    <Route
      index
      element={
        NEW_UI_PUBLIC_LANDING ? (
          <LandingPage />
        ) : (
          <RequireV2Access>
            <LandingPage />
          </RequireV2Access>
        )
      }
    />
    <Route
      path="app/*"
      element={
        <RequireV2Access>
          <NewUiApp />
        </RequireV2Access>
      }
    />
    <Route path="*" element={<Navigate to={V2_LANDING} replace />} />
  </Routes>
);

export default NewUiEntry;
